import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { deleteAllConnectAccounts } from "@/utils/stripeConnectUtils";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock, Mail, CreditCard, ExternalLink, CheckCircle, AlertCircle, RefreshCw, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";

// Schema for password change
const passwordSchema = z
  .object({
    currentPassword: z.string().min(6, "Password must be at least 6 characters"),
    newPassword: z.string().min(6, "Password must be at least 6 characters"),
    confirmNewPassword: z.string().min(6, "Password must be at least 6 characters"),
  })
  .refine(data => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ["confirmNewPassword"],
  });

// Define types for Stripe Connect status
type ConnectStatus = {
  accountId: string;
  onboardingCompleted: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  requiresAction: boolean;
  currentlyDue: string[];
};

const Account = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isConnectLoading, setIsConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null | undefined>(undefined);
  const [hasConnectAccount, setHasConnectAccount] = useState<boolean | undefined>(undefined);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [statusPolling, setStatusPolling] = useState(false);
  const [isDeletingAccounts, setIsDeletingAccounts] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [isLoadingConnectStatus, setIsLoadingConnectStatus] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState({
    email_appointment_reminders: true,
    email_notifications_enabled: true
  });
  const [isLoadingEmailPrefs, setIsLoadingEmailPrefs] = useState(true);
  const [isSavingEmailPrefs, setIsSavingEmailPrefs] = useState(false);
  
  // Check if user is a mentor or has both profile types
  const [showPayoutSettings, setShowPayoutSettings] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    } else if (user) {
      // Determine if payout settings should be shown based on profile type
      setShowPayoutSettings(
        user.profileType === "mentor" || user.profileType === "both"
      );
      
      // Load email preferences
      loadEmailPreferences();
      
      // Load Stripe Connect status if user is a mentor or has both profile types
      if (user.profileType === "mentor" || user.profileType === "both") {
        loadConnectStatus();
      }
      
      // Check for Stripe return parameters
      const success = searchParams.get('success');
      const refresh = searchParams.get('refresh');
      
      if (success === 'true') {
        toast({
          title: "Welcome back!",
          description: "Checking your Stripe Connect account status...",
        });
        // Start polling to check for updates
        startStatusPolling();
      } else if (refresh === 'true') {
        toast({
          title: "Setup Required",
          description: "Please complete your Stripe Connect account setup.",
          variant: "destructive",
        });
      }
    }
  }, [loading, user, navigate, searchParams, toast]);

  const startStatusPolling = () => {
    if (statusPolling) return;
    
    setStatusPolling(true);
    console.log("🔄 Starting status polling...");
    
    const pollInterval = setInterval(async () => {
      console.log("🔍 Polling Stripe Connect status...");
      await loadConnectStatus();
      
      // Stop polling after checking status
      clearInterval(pollInterval);
      setStatusPolling(false);
    }, 2000);
    
    // Stop polling after 30 seconds max
    setTimeout(() => {
      clearInterval(pollInterval);
      setStatusPolling(false);
      console.log("⏰ Status polling timeout");
    }, 30000);
  };

  const loadConnectStatus = async () => {
    try {
      console.log("🔍 Loading Stripe Connect status...");
      setConnectError(null);
      setIsLoadingConnectStatus(true);
      
      const { data, error } = await supabase.functions.invoke('get-connect-status');
      
      console.log("📊 Connect status response:", { data, error });
      
      if (error) {
        console.error("❌ Supabase function error:", error);
        setConnectError(`Failed to load status: ${error.message}`);
        setHasConnectAccount(false);
        setConnectStatus(null);
        return;
      }

      if (data?.error) {
        console.error("❌ Function returned error:", data.error);
        setConnectError(`Status error: ${data.error}`);
        setHasConnectAccount(false);
        setConnectStatus(null);
        return;
      }

      console.log("✅ Successfully loaded connect status:", data);
      setHasConnectAccount(data.hasAccount);
      setConnectStatus(data.status);
    } catch (error) {
      console.error("💥 Exception in loadConnectStatus:", error);
      setConnectError(`Exception: ${error instanceof Error ? error.message : String(error)}`);
      setHasConnectAccount(false);
      setConnectStatus(null);
    } finally {
      setIsLoadingConnectStatus(false);
    }
  };

  const loadEmailPreferences = async () => {
    try {
      setIsLoadingEmailPrefs(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('email_appointment_reminders, email_notifications_enabled')
        .eq('id', user?.id)
        .single();

      if (error) {
        console.error('Error loading email preferences:', error);
        return;
      }

      if (data) {
        setEmailPreferences({
          email_appointment_reminders: data.email_appointment_reminders ?? true,
          email_notifications_enabled: data.email_notifications_enabled ?? true
        });
      }
    } catch (error) {
      console.error('Exception loading email preferences:', error);
    } finally {
      setIsLoadingEmailPrefs(false);
    }
  };

  const handleEmailPreferenceChange = async (field: string, value: boolean) => {
    // Update local state immediately for better UX
    setEmailPreferences(prev => ({ ...prev, [field]: value }));
    
    try {
      setIsSavingEmailPrefs(true);
      const { error } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', user?.id);

      if (error) {
        // Revert local state on error
        setEmailPreferences(prev => ({ ...prev, [field]: !value }));
        toast({
          title: "Error",
          description: "Failed to save email preference",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Preference Updated",
        description: "Your email preference has been saved",
      });
    } catch (error) {
      // Revert local state on error
      setEmailPreferences(prev => ({ ...prev, [field]: !value }));
      console.error('Error updating email preference:', error);
      toast({
        title: "Error",
        description: "An error occurred while saving your preference",
        variant: "destructive",
      });
    } finally {
      setIsSavingEmailPrefs(false);
    }
  };

  const handleSetupStripeConnect = async () => {
    setIsConnectLoading(true);
    setConnectError(null);
    
    try {
      console.log("🚀 Starting Stripe Connect setup...");
      
      const { data, error } = await supabase.functions.invoke('create-account-link');
      
      console.log("📊 Account link response:", { data, error });
      
      if (error) {
        console.error("❌ Account link error:", error);
        const errorMsg = error.message || "Failed to create account link";
        setConnectError(errorMsg);
        toast({
          title: "Setup Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        console.error("❌ Account link creation error:", data.error);
        const errorMsg = `${data.error}${data.details ? `: ${data.details}` : ''}`;
        setConnectError(errorMsg);
        toast({
          title: "Setup Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      if (data?.success && data.url) {
        console.log("🔗 Opening Stripe onboarding in new tab:", data.url);
        
        // Validate the URL is a proper Stripe Connect URL
        if (!data.url.startsWith('https://connect.stripe.com/')) {
          const errorMsg = "Invalid Stripe Connect URL received";
          setConnectError(errorMsg);
          toast({
            title: "Setup Error", 
            description: errorMsg,
            variant: "destructive",
          });
          return;
        }
        
        // Open in new tab to bypass security restrictions
        const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
        
        if (!newWindow) {
          // Popup blocked - provide fallback
          setConnectError("Popup blocked. Please click the link below to continue setup.");
          toast({
            title: "Popup Blocked",
            description: "Please allow popups or use the manual link below to complete setup.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Stripe Setup Opened",
            description: "Complete your setup in the new tab. Return here when finished and refresh your status.",
          });
          
          // Start polling for status updates
          setTimeout(() => {
            startStatusPolling();
          }, 5000); // Give user time to complete setup
        }
      } else {
        const errorMsg = "No onboarding URL received from Stripe";
        setConnectError(errorMsg);
        toast({
          title: "Setup Error", 
          description: errorMsg,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to setup Stripe Connect";
      console.error("💥 Exception in handleSetupStripeConnect:", errorMessage);
      setConnectError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsConnectLoading(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    setIsLoadingDashboard(true);
    setDashboardError(null);
    
    try {
      console.log("🚀 Opening Stripe dashboard...");
      
      const { data, error } = await supabase.functions.invoke('create-stripe-login-link');
      
      console.log("📊 Dashboard link response:", { data, error });
      
      if (error) {
        console.error("❌ Dashboard link error:", error);
        const errorMsg = error.message || "Failed to create dashboard link";
        setDashboardError(errorMsg);
        toast({
          title: "Dashboard Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        console.error("❌ Dashboard link creation error:", data.error);
        const errorMsg = data.error;
        setDashboardError(errorMsg);
        toast({
          title: "Dashboard Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      if (data?.success && data.url) {
        console.log("🔗 Opening Stripe dashboard in new tab:", data.url);
        
        // Open Stripe dashboard in new tab
        const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
        
        if (!newWindow) {
          // Popup blocked
          setDashboardError("Popup blocked. Please allow popups to access your Stripe dashboard.");
          toast({
            title: "Popup Blocked",
            description: "Please allow popups to access your Stripe dashboard.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Dashboard Opened",
            description: "Your Stripe dashboard has been opened in a new tab.",
          });
        }
      } else {
        const errorMsg = "No dashboard URL received from Stripe";
        setDashboardError(errorMsg);
        toast({
          title: "Dashboard Error", 
          description: errorMsg,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to open Stripe dashboard";
      console.error("💥 Exception in handleViewStripeDashboard:", errorMessage);
      setDashboardError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  const handleDeleteAllConnectAccounts = async () => {
    if (!window.confirm(
      "⚠️ WARNING: This will permanently delete ALL Stripe Connect accounts from both Stripe and the database. This action cannot be undone. Are you sure you want to proceed?"
    )) {
      return;
    }

    setIsDeletingAccounts(true);
    try {
      console.log("🗑️ Starting Connect account deletion...");
      const result = await deleteAllConnectAccounts();
      
      if (result.success) {
        console.log("✅ Connect accounts deleted successfully");
        toast({
          title: "Connect Accounts Deleted",
          description: `Successfully deleted ${result.results?.successful || 0} Connect accounts. ${result.results?.errors || 0} errors occurred.`,
        });
        
        // Clear local state
        setHasConnectAccount(false);
        setConnectStatus(null);
        setConnectError(null);
        
        // Refresh the Connect status after deletion
        setTimeout(() => {
          loadConnectStatus();
        }, 1000);
      } else {
        console.error("❌ Connect account deletion failed:", result.error);
        toast({
          title: "Deletion Failed",
          description: result.error || "Failed to delete Connect accounts",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("💥 Error deleting Connect accounts:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting accounts",
        variant: "destructive",
      });
    } finally {
      setIsDeletingAccounts(false);
    }
  };

  // Password change form
  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const onPasswordSubmit = async (values: z.infer<typeof passwordSchema>) => {
    if (!user) return;
    
    setIsPasswordLoading(true);
    
    try {
      // First verify current password by attempting to sign in
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: values.currentPassword,
      });
      
      if (verifyError) {
        toast({
          title: "Error",
          description: "Current password is incorrect",
          variant: "destructive",
        });
        setIsPasswordLoading(false);
        return;
      }
      
      // Update password
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password Updated",
        description: "Your password has been changed successfully",
      });
      
      passwordForm.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const renderPayoutStatus = () => {
    // Show authentication required message if not logged in
    if (!user) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Authentication Required</span>
          </div>
          <p className="text-sm text-gray-600">
            Please log in to access Stripe Connect features.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="w-full md:w-auto"
          >
            Go to Login
          </Button>
        </div>
      );
    }

    // Show loading skeleton while status is being fetched
    if (connectStatus === undefined && hasConnectAccount === undefined) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-600">
              {isLoadingConnectStatus || statusPolling ? "Checking for updates..." : "Loading account status..."}
            </span>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      );
    }

    if (connectError) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Setup Error</span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{connectError}</p>
          </div>
          
          {connectError.includes("Popup blocked") && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2">
                Manual Setup: Click the button below to open Stripe Connect directly
              </p>
              <Button 
                onClick={handleSetupStripeConnect}
                disabled={isConnectLoading}
                size="sm"
                variant="outline"
              >
                {isConnectLoading ? "Getting Link..." : "Open Stripe Connect"}
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button 
              onClick={loadConnectStatus}
              variant="outline"
              size="sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            <Button 
              onClick={handleSetupStripeConnect}
              disabled={isConnectLoading}
              size="sm"
            >
              {isConnectLoading ? "Setting up..." : "Try Setup Again"}
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      );
    }

    // Unified "Setup Required" state - only show if confirmed by API
    const requiresSetup = hasConnectAccount === false || (hasConnectAccount === true && connectStatus && !connectStatus.onboardingCompleted);
    
    if (requiresSetup) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Setup Required</span>
          </div>
          <p className="text-sm text-gray-600">
            Set up your Stripe Connect account to receive payments securely and manage your payouts.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-amber-800">
              <CreditCard className="h-4 w-4" />
              <span className="text-sm font-medium">Setup Instructions</span>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              <strong>Setup will open in a new tab.</strong> Return here when finished and refresh your status.
            </p>
          </div>
          
          <Button 
            onClick={handleSetupStripeConnect}
            disabled={isConnectLoading}
            className="w-full md:w-auto"
          >
            {isConnectLoading ? "Opening Setup..." : "Setup Stripe Connect"}
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </div>
      );
    }

    // Connected & Ready state - only show if confirmed by API
    if (hasConnectAccount === true && connectStatus && connectStatus.onboardingCompleted) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Connected & Ready</span>
              </div>
              <div className="text-sm text-gray-600">
                Your Stripe Connect account is fully configured and ready to receive payments
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-gray-600">Payouts Status</div>
              <div className={`font-medium ${connectStatus.payoutsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {connectStatus.payoutsEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-gray-600">Charges Status</div>
              <div className={`font-medium ${connectStatus.chargesEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {connectStatus.chargesEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>
          
          {connectStatus.requiresAction && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Action Required</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                Please complete additional verification steps to maintain your account status.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSetupStripeConnect}
                disabled={isConnectLoading}
                className="mt-2"
              >
                Update Account
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
          
          {dashboardError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{dashboardError}</p>
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-800">
              <CreditCard className="h-4 w-4" />
              <span className="text-sm font-medium">Payout Information</span>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Payments from your mentoring sessions are automatically processed and transferred 
              to your bank account according to your Stripe payout schedule.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={loadConnectStatus}
              variant="outline"
              size="sm"
              disabled={statusPolling || isLoadingConnectStatus}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${statusPolling || isLoadingConnectStatus ? 'animate-spin' : ''}`} />
              {statusPolling || isLoadingConnectStatus ? 'Checking...' : 'Refresh Status'}
            </Button>
            <Button 
              onClick={handleViewStripeDashboard}
              variant="outline"
              size="sm"
              disabled={isLoadingDashboard}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {isLoadingDashboard ? 'Opening Dashboard...' : 'View Stripe Dashboard'}
            </Button>
          </div>
        </div>
      );
    }

    // Fallback state - should rarely be reached
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-gray-600">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">Status Unknown</span>
        </div>
        <p className="text-sm text-gray-600">
          Unable to determine account status. Please try refreshing.
        </p>
        <Button 
          onClick={loadConnectStatus}
          variant="outline"
          size="sm"
          disabled={isLoadingConnectStatus}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingConnectStatus ? 'animate-spin' : ''}`} />
          {isLoadingConnectStatus ? 'Loading...' : 'Refresh Status'}
        </Button>
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="w-12 h-12 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex-1 flex flex-col">
        <PageHeader 
          title="Account Settings"
          subtitle="Manage your account settings and payout preferences"
        />
        
        <main className="max-w-7xl mx-auto py-6 px-4 flex-1">
        <Tabs defaultValue="email" className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar Navigation - Desktop */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-6">
              <Card className="shadow-sm">
                <CardContent className="p-2">
                  <TabsList className="flex-col h-auto w-full bg-transparent p-0 space-y-1">
                    <TabsTrigger 
                      value="email" 
                      className="w-full justify-start gap-3 px-4 py-3 text-left rounded-md data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors min-h-[48px]"
                    >
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span>Email Address</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="password" 
                      className="w-full justify-start gap-3 px-4 py-3 text-left rounded-md data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors min-h-[48px]"
                    >
                      <Lock className="h-4 w-4 flex-shrink-0" />
                      <span>Password</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notifications" 
                      className="w-full justify-start gap-3 px-4 py-3 text-left rounded-md data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors min-h-[48px]"
                    >
                      <Bell className="h-4 w-4 flex-shrink-0" />
                      <span>Notifications</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="payouts" 
                      className={`w-full justify-start gap-3 px-4 py-3 text-left rounded-md data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors min-h-[48px] ${!showPayoutSettings ? 'hidden' : ''}`}
                    >
                      <CreditCard className="h-4 w-4 flex-shrink-0" />
                      <span>Payouts</span>
                    </TabsTrigger>
                  </TabsList>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mobile/Tablet Top Navigation */}
          <div className="lg:hidden mb-6">
            <Card>
              <CardContent className="p-2">
                <TabsList className="w-full h-auto grid grid-cols-4 bg-gray-100 p-1 gap-1">
                  <TabsTrigger 
                    value="email" 
                    className="flex flex-col gap-1 py-3 px-1 text-center data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors rounded-md"
                  >
                    <Mail className="h-4 w-4 mx-auto" />
                    <span className="text-xs leading-tight">Email</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="password" 
                    className="flex flex-col gap-1 py-3 px-1 text-center data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors rounded-md"
                  >
                    <Lock className="h-4 w-4 mx-auto" />
                    <span className="text-xs leading-tight">Password</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notifications" 
                    className="flex flex-col gap-1 py-3 px-1 text-center data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors rounded-md"
                  >
                    <Bell className="h-4 w-4 mx-auto" />
                    <span className="text-xs leading-tight">Notify</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="payouts" 
                    className={`flex flex-col gap-1 py-3 px-1 text-center data-[state=active]:bg-emu-blue data-[state=active]:text-white data-[state=active]:font-semibold hover:bg-emu-blue/10 transition-colors rounded-md ${!showPayoutSettings ? 'hidden' : ''}`}
                  >
                    <CreditCard className="h-4 w-4 mx-auto" />
                    <span className="text-xs leading-tight">Payouts</span>
                  </TabsTrigger>
                </TabsList>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {/* Email Tab */}
            <TabsContent value="email" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-emu-blue" />
                    Email Address
                  </CardTitle>
                  <CardDescription>
                    Your registered email address (cannot be changed)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-1">
                      <div className="font-medium">Current Email Address</div>
                      <div className="bg-gray-50 border rounded p-3 text-gray-800">
                        {user?.email || "Not available"}
                      </div>
                      <div className="text-sm text-gray-600">
                        This is the email address associated with your EMU Mentoring account
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Mail className="h-4 w-4" />
                        <span className="text-sm font-medium">Email Address Policy</span>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">
                        For security reasons, email addresses cannot be changed directly. If you need to update your email, 
                        please contact our support team who can assist you with the verification process.
                      </p>
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-amber-800">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Important</span>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        Make sure you have access to this email address as it's used for account recovery, 
                        important notifications, and appointment reminders.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Password Tab */}
            <TabsContent value="password" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-emu-blue" />
                    Change Password
                  </CardTitle>
                  <CardDescription>
                    Update your account password
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Lock className="h-4 w-4" />
                        <span className="text-sm font-medium">Password Requirements</span>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">
                        Passwords must be at least 6 characters long. For better security, 
                        use a mix of letters, numbers, and special characters.
                      </p>
                    </div>
                    
                    <div>
                      <div className="font-medium mb-4">Update Your Password</div>
                      <Form {...passwordForm}>
                        <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                          <FormField
                            control={passwordForm.control}
                            name="currentPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Current Password</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="password" 
                                    placeholder="Enter your current password" 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={passwordForm.control}
                            name="newPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>New Password</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="password" 
                                    placeholder="Enter your new password" 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={passwordForm.control}
                            name="confirmNewPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Confirm New Password</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="password" 
                                    placeholder="Confirm your new password" 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <Button 
                            type="submit" 
                            className="w-full md:w-auto"
                            disabled={isPasswordLoading}
                          >
                            {isPasswordLoading ? "Updating..." : "Update Password"}
                          </Button>
                        </form>
                      </Form>
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-amber-800">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Security Notice</span>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        After changing your password, you'll remain logged in on this device. 
                        You may need to log in again on other devices using your new password.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Notifications Tab */}
            <TabsContent value="notifications" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-emu-blue" />
                    Email Preferences
                  </CardTitle>
                  <CardDescription>
                    Manage your email notification settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingEmailPrefs ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">Loading preferences...</span>
                      </div>
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="font-medium">Email Notifications</div>
                          <div className="text-sm text-gray-600">
                            Master switch for all email notifications from EMU Mentoring
                          </div>
                        </div>
                        <Switch
                          checked={emailPreferences.email_notifications_enabled}
                          onCheckedChange={(checked) => 
                            handleEmailPreferenceChange('email_notifications_enabled', checked)
                          }
                          disabled={isSavingEmailPrefs}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="font-medium">Appointment Reminders</div>
                          <div className="text-sm text-gray-600">
                            Receive email reminders 24 hours before scheduled sessions
                          </div>
                        </div>
                        <Switch
                          checked={emailPreferences.email_appointment_reminders && emailPreferences.email_notifications_enabled}
                          onCheckedChange={(checked) => 
                            handleEmailPreferenceChange('email_appointment_reminders', checked)
                          }
                          disabled={isSavingEmailPrefs || !emailPreferences.email_notifications_enabled}
                        />
                      </div>
                      
                      {!emailPreferences.email_notifications_enabled && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-amber-800">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Notifications Disabled</span>
                          </div>
                          <p className="text-sm text-amber-700 mt-1">
                            Enable email notifications to receive appointment reminders and other important updates.
                          </p>
                        </div>
                      )}
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-blue-800">
                          <Mail className="h-4 w-4" />
                          <span className="text-sm font-medium">How Email Reminders Work</span>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">
                          You'll receive appointment reminder emails 24 hours before each scheduled session. 
                          These emails include session details, preparation tips, and quick access to your dashboard.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Payouts Tab */}
            {showPayoutSettings && (
              <TabsContent value="payouts" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-emu-blue" />
                      Payout Settings
                    </CardTitle>
                    <CardDescription>
                      Manage how you receive payments for your mentoring services
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {renderPayoutStatus()}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </div>
        </Tabs>

        {/* Admin Tools Section - only show for admins */}
        {user?.isAdmin && (
          <Card className="mt-8">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-red-600">Admin Tools</h2>
              <div className="space-y-4">
                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <h3 className="font-medium text-red-800 mb-2">Delete All Stripe Connect Accounts</h3>
                  <p className="text-sm text-red-700 mb-3">
                    This will permanently delete ALL Stripe Connect accounts from both Stripe and the database. 
                    This action cannot be undone and should only be used for testing or cleanup purposes.
                  </p>
                  <Button
                    onClick={handleDeleteAllConnectAccounts}
                    disabled={isDeletingAccounts}
                    variant="destructive"
                    size="sm"
                  >
                    {isDeletingAccounts ? "Deleting..." : "Delete All Connect Accounts"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default Account;
